import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "../../../@/components/ui/button";

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  showInfo?: boolean;
  itemName?: string;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  showInfo = true,
  itemName = "éléments",
  className = "",
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1140); // Ajusté à 1100px pour une meilleure transition
    };

    // Check initial size
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Ne pas afficher la pagination s'il n'y a qu'une seule page
  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getVisiblePages = () => {
    const pages: (number | "ellipsis")[] = [];

    // Réduire le nombre de pages visibles sur mobile
    const maxPages = isMobile ? 3 : 7;

    if (totalPages <= maxPages) {
      // Si moins de pages que le maximum, afficher toutes les pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Toujours afficher la première page
      pages.push(1);

      if (isMobile) {
        // Mode mobile : affichage simplifié mais intelligent
        if (totalPages <= 5) {
          // Si 5 pages ou moins, on peut toutes les afficher
          for (let i = 2; i <= totalPages; i++) {
            pages.push(i);
          }
        } else if (currentPage <= 2) {
          // Au début : 1, 2, 3, ..., last
          pages.push(2);
          if (totalPages > 3) {
            pages.push(3);
            pages.push("ellipsis");
          }
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 1) {
          // À la fin : 1, ..., before-last, last
          pages.push("ellipsis");
          if (totalPages > 3) {
            pages.push(totalPages - 1);
          }
          pages.push(totalPages);
        } else {
          // Au milieu : 1, ..., current, ..., last
          pages.push("ellipsis");
          pages.push(currentPage);
          pages.push("ellipsis");
          pages.push(totalPages);
        }
      } else {
        // Mode desktop : affichage complet
        if (currentPage <= 3) {
          // Au début : 1, 2, 3, ..., last
          for (let i = 2; i <= 3; i++) {
            pages.push(i);
          }
          pages.push("ellipsis");
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          // À la fin : 1, ..., before2, before1, last
          pages.push("ellipsis");
          for (let i = totalPages - 2; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          // Au milieu : 1, ..., current-1, current, current+1, ..., last
          pages.push("ellipsis");
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push("ellipsis");
          pages.push(totalPages);
        }
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className={className}>
      {/* Layout responsive basé sur la logique JavaScript */}
      {isMobile ? (
        // Layout compact pour écrans < 1100px
        <div className="space-y-4">
          {showInfo && (
            <div className="text-sm text-muted-foreground text-center">
              {startItem}-{endItem} sur {totalItems}
            </div>
          )}

          {/* Navigation compacte */}
          <div className="flex items-center justify-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1 px-2"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            <div className="flex items-center gap-1 mx-2">
              {visiblePages.map((page, index) => {
                if (page === "ellipsis") {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-1 text-muted-foreground text-xs"
                    >
                      ...
                    </span>
                  );
                }

                return (
                  <Button
                    key={page}
                    variant={page === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(page)}
                    className="w-7 h-7 p-0 text-xs"
                  >
                    {page}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="flex items-center gap-1 px-2"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ) : (
        // Layout horizontal pour écrans >= 1100px
        <div className="flex items-center justify-between">
          {showInfo && (
            <div className="text-sm text-muted-foreground">
              {startItem} - {endItem} sur {totalItems} {itemName}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            <div className="flex items-center gap-1">
              {visiblePages.map((page, index) => {
                if (page === "ellipsis") {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 text-muted-foreground"
                    >
                      ...
                    </span>
                  );
                }

                return (
                  <Button
                    key={page}
                    variant={page === currentPage ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(page)}
                    className="w-8 h-8 p-0"
                  >
                    {page}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                onPageChange(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="flex items-center gap-1"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pagination;
